import React, { createContext, useContext, useEffect, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { translate } from "../translate/translate";

type TitleFunction = (param?: string) => string;

interface Titles {
    [key: string]: TitleFunction;
}

interface DocumentTitleContextType {
    setDocumentTitle: (param?: string) => void;
}

const DocumentTitleContext = createContext<DocumentTitleContextType | undefined>(undefined);

const extractPrefix = (path: string): string => {
    const segments = path.split("/");
    return `/${segments[1]}/${segments[2]}`; // Pega o prefixo da rota
};

const titles: Titles = {
    "/account/user": () => `${translate.t("user")} | PlanMat`,
    "/account/group" : () => `${translate.t("group")} | PlanMat`,
    "/general/analysis" : () => `${translate.t("analysis")} | PlanMat`,
    "/general/bom": (bomNumber) => bomNumber
        ? `${translate.t("bom")}: ${bomNumber} | PlanMat`
        : `${translate.t("bom")} | PlanMat`,
    "/general/customer": (customerName) => customerName
        ? `${translate.t("customer")}: ${customerName} | PlanMat`
        : `${translate.t("customers")} | PlanMat`,
    "/general/stock": () => `${translate.t("stock")} | PlanMat`,
    "/general/forecast": (customerName) => customerName
        ? `${translate.t("forecast")}: ${customerName} | PlanMat`
        : `${translate.t("forecast")} | PlanMat`,
    "/general/po": () => `${translate.t("purchase_order")} | PlanMat`,
    "/general/import-spreadsheets": () => `${translate.t("import_spreadsheets")} | PlanMat`,
    "/general/help": () => `${translate.t("user_manual")} | PlanMat`,
};

export const DocumentTitleProvider = ({ children }: { children: ReactNode }) => {
    const location = useLocation();

    const setDocumentTitle = (param?: string) => {
        const path = extractPrefix(location.pathname);
        const titleFn = titles[path];

        if (titleFn) {
            document.title = param ? titleFn(param) : titleFn();
        } else {
            document.title = "PlanMat";
        }
    };

    useEffect(() => {
        setDocumentTitle();
    }, [location.pathname]);

    return (
        <DocumentTitleContext.Provider value={{ setDocumentTitle }}>
            {children}
        </DocumentTitleContext.Provider>
    );
};

export const useDocumentTitle = () => {
    const context = useContext(DocumentTitleContext);
    if (!context) {
        throw new Error("useTitle must be used within a TitleProvider");
    }
    return context;
};
